import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { tenantDataAPI } from '../services/api'
import {
  Handshake,
  Plus,
  Pencil,
  Ban,
  Loader2,
  AlertCircle,
  CalendarClock,
  History,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'

/**
 * Super-admin collaboration-program panel for a tenant.
 *
 * Adding a tenant to a program releases their access immediately for a fixed
 * window, regardless of payment, and shields them from payment-driven
 * deactivation for its duration. When the window closes, the nightly job hands
 * the tenant back to the ordinary payment rules.
 */

const DEFAULT_DURATIONS = [1, 3, 6, 12]

function apiErrorMessage(err, fallback = 'Algo deu errado. Tente novamente.') {
  const data = err?.response?.data
  if (typeof data?.error === 'string') return data.error
  if (data?.error && typeof data.error === 'object') return data.error.message || data.error.code || fallback
  return data?.message || err?.message || fallback
}

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—')

const STATUS_LABEL = { active: 'Ativa', expired: 'Expirada', revoked: 'Revogada' }

/** Local-timezone yyyy-mm-dd, so the <input type="date"> value matches what the admin sees. */
const toDateInput = (date) => {
  const offset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - offset).toISOString().slice(0, 10)
}

/** Mirrors the server's clamping rule: 31 Jan + 1 month -> 28/29 Feb, never 3 Mar. */
const addMonths = (date, months) => {
  const result = new Date(date.getTime())
  const targetMonth = result.getMonth() + months
  const lastDay = new Date(result.getFullYear(), targetMonth + 1, 0).getDate()
  result.setDate(Math.min(result.getDate(), lastDay))
  result.setMonth(targetMonth)
  return result
}

const statusChip = (status) => {
  const styles = {
    active: 'bg-green-100 text-green-700',
    expired: 'bg-admin-100 text-admin-700',
    revoked: 'bg-orange-100 text-orange-700',
  }
  return `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.expired}`
}

const ProgramModal = ({ mode, affiliation, programs, durationChoices, onSubmit, onClose }) => {
  const isEdit = mode === 'edit'
  const [programId, setProgramId] = useState(
    affiliation?.program_id || programs[0]?.id || ''
  )
  const [durationMonths, setDurationMonths] = useState(() => {
    if (isEdit) return 'custom'
    const program = programs[0]
    return program?.default_duration_months || 12
  })
  const [customEndDate, setCustomEndDate] = useState(() =>
    affiliation?.ends_at ? toDateInput(new Date(affiliation.ends_at)) : ''
  )
  const [notes, setNotes] = useState(affiliation?.notes || '')
  const [acknowledged, setAcknowledged] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Picking a program adopts its configured default term.
  const handleProgramChange = (nextId) => {
    setProgramId(nextId)
    const program = programs.find((p) => p.id === nextId)
    if (program?.default_duration_months) setDurationMonths(program.default_duration_months)
  }

  const startsAt = useMemo(
    () => (isEdit && affiliation ? new Date(affiliation.starts_at) : new Date()),
    [isEdit, affiliation]
  )

  const computedEndDate = useMemo(() => {
    if (durationMonths === 'custom') return customEndDate ? new Date(customEndDate) : null
    return addMonths(startsAt, Number(durationMonths))
  }, [durationMonths, customEndDate, startsAt])

  const handleSubmit = async () => {
    setError('')
    if (!isEdit && !programId) return setError('Selecione um programa.')
    if (durationMonths === 'custom' && !customEndDate) return setError('Escolha uma data de término.')
    if (computedEndDate && computedEndDate <= startsAt) {
      return setError('A data de término deve ser posterior à data de início.')
    }
    if (!acknowledged) return setError('Confirme que o acesso será liberado.')

    setSaving(true)
    try {
      const payload =
        durationMonths === 'custom'
          ? { ends_at: customEndDate, notes: notes.trim() }
          : { duration_months: Number(durationMonths), notes: notes.trim() }
      if (!isEdit) payload.program_id = programId
      await onSubmit(payload)
      onClose()
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
        <div className="relative w-full max-w-xl rounded-xl bg-white shadow-2xl">
          <div className="flex items-center gap-3 border-b border-admin-200 p-6">
            <div className="rounded-lg bg-primary-100 p-2">
              <Handshake className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-admin-900">
                {isEdit ? 'Editar vínculo com o programa' : 'Adicionar ao programa'}
              </h2>
              <p className="text-sm text-admin-600">
                {isEdit
                  ? 'Altere por quanto tempo esta parceria cobre o estúdio.'
                  : 'Libere o acesso por um período fixo como parte de uma parceria.'}
              </p>
            </div>
          </div>

          <div className="space-y-5 p-6">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="flex items-center">
                  <AlertCircle className="mr-2 h-5 w-5 text-red-500" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-admin-700">Programa *</label>
              <select
                className="input"
                value={programId}
                onChange={(e) => handleProgramChange(e.target.value)}
                disabled={isEdit || saving}
              >
                {programs.length === 0 && <option value="">Nenhum programa ativo configurado</option>}
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                    {program.partner_name ? ` — ${program.partner_name}` : ''}
                  </option>
                ))}
              </select>
              {isEdit && (
                <p className="mt-1 text-xs text-admin-500">
                  O programa não pode ser alterado. Desative este vínculo e adicione um novo.
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-admin-700">
                Acesso liberado por *
              </label>
              <select
                className="input"
                value={durationMonths}
                onChange={(e) =>
                  setDurationMonths(e.target.value === 'custom' ? 'custom' : Number(e.target.value))
                }
                disabled={saving}
              >
                {(durationChoices?.length ? durationChoices : DEFAULT_DURATIONS).map((months) => (
                  <option key={months} value={months}>
                    {months} {months === 1 ? 'mês' : 'meses'}
                  </option>
                ))}
                <option value="custom">Data de término personalizada…</option>
              </select>

              {durationMonths === 'custom' && (
                <input
                  type="date"
                  className="input mt-2"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  disabled={saving}
                />
              )}

              <p className="mt-2 flex items-center gap-1.5 text-sm text-admin-600">
                <CalendarClock className="h-4 w-4" />
                Acesso liberado até{' '}
                <strong className="text-admin-900">
                  {computedEndDate ? computedEndDate.toLocaleDateString('pt-BR') : '—'}
                </strong>
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-admin-700">Observações</label>
              <textarea
                rows={3}
                className="input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Turma, contato no parceiro, qualquer coisa que valha lembrar depois"
                disabled={saving}
              />
            </div>

            {!isEdit && (
              <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  disabled={saving}
                />
                <span className="text-sm text-amber-800">
                  Isso libera o acesso completo imediatamente, mesmo sem pagamento. Se o estúdio nunca
                  foi ativado, o proprietário receberá as credenciais de acesso por e-mail.
                </span>
              </label>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-admin-200 p-6">
            <button onClick={onClose} className="btn btn-secondary" disabled={saving}>
              Cancelar
            </button>
            <button onClick={handleSubmit} className="btn btn-primary" disabled={saving}>
              {saving ? (
                <span className="flex items-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </span>
              ) : isEdit ? (
                'Salvar alterações'
              ) : (
                'Adicionar ao programa'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const TenantProgramPanel = ({ tenantId, onChange, readOnly = false }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [modal, setModal] = useState(null) // 'add' | 'edit'
  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await tenantDataAPI.getProgramAffiliation(tenantId)
      setData(res.data.data)
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao carregar o vínculo com o programa'))
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    load()
  }, [load])

  const active = data?.active
  const programs = data?.available_programs || []
  // Superseded/expired rows only; the active one has its own card above.
  const pastAffiliations = (data?.history || []).filter((a) => a.id !== active?.id)

  const handleCreate = async (payload) => {
    await tenantDataAPI.createProgramAffiliation(tenantId, payload)
    await load()
    onChange?.()
  }

  const handleUpdate = async (payload) => {
    await tenantDataAPI.updateProgramAffiliation(tenantId, active.id, payload)
    await load()
    onChange?.()
  }

  const handleRevoke = async () => {
    setRevoking(true)
    setError('')
    try {
      await tenantDataAPI.revokeProgramAffiliation(tenantId, active.id)
      setConfirmRevoke(false)
      await load()
      onChange?.()
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao desativar o vínculo'))
    } finally {
      setRevoking(false)
    }
  }

  const daysLeft = active?.days_left ?? 0
  const expired = daysLeft < 0
  const countdownClass = expired
    ? 'text-red-600'
    : daysLeft < 30
      ? 'text-amber-600'
      : 'text-admin-900'

  return (
    <div className="card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-admin-900">
          <Handshake className="h-5 w-5 text-primary-600" />
          Programa de colaboração
        </h3>
        {!loading && active && !readOnly && (
          <div className="flex items-center gap-2">
            <button onClick={() => setModal('edit')} className="btn btn-secondary">
              <Pencil className="mr-2 h-4 w-4" />
              Editar
            </button>
            <button
              onClick={() => setConfirmRevoke(true)}
              className="btn btn-secondary text-red-600 hover:bg-red-50"
            >
              <Ban className="mr-2 h-4 w-4" />
              Desativar
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-admin-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Carregando…
        </div>
      ) : active ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-admin-600">Programa</label>
            <p className="font-medium text-admin-900">{active.program_name}</p>
            {active.partner_name && (
              <p className="text-sm text-admin-600">{active.partner_name}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-admin-600">Período</label>
            <p className="text-admin-900">
              {formatDate(active.starts_at)} → {formatDate(active.ends_at)}
            </p>
            <p className={`text-sm font-medium ${countdownClass}`}>
              {expired
                ? `Expirou há ${Math.abs(daysLeft)}d — aguardando a rotina noturna`
                : `${daysLeft}d restantes de acesso liberado`}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-admin-600">Observações</label>
            <p className="text-admin-900">{active.notes || '—'}</p>
            {active.created_by && (
              <p className="mt-1 text-xs text-admin-500">Adicionado por {active.created_by}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="py-6 text-center">
          <Handshake className="mx-auto mb-3 h-10 w-10 text-admin-300" />
          <p className="mb-1 text-admin-700">Este estúdio não faz parte de um programa de colaboração.</p>
          <p className="mb-4 text-sm text-admin-500">
            O acesso segue as regras normais de pagamento.
          </p>
          {!readOnly && (
          <button
            onClick={() => setModal('add')}
            className="btn btn-primary"
            disabled={programs.length === 0}
          >
            <Plus className="mr-2 h-4 w-4" />
            Adicionar ao programa
          </button>
          )}
          {!readOnly && programs.length === 0 && (
            <p className="mt-2 text-xs text-admin-500">
              Nenhum programa ativo configurado — adicione um em Configurações primeiro.
            </p>
          )}
        </div>
      )}

      {pastAffiliations.length > 0 && (
        <div className="mt-5 border-t border-admin-100 pt-4">
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="flex items-center gap-1.5 text-sm font-medium text-admin-600 hover:text-admin-800"
          >
            {showHistory ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <History className="h-4 w-4" />
            Histórico ({pastAffiliations.length})
          </button>

          {showHistory && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead>
                  <tr className="text-admin-500">
                    <th className="pb-2 pr-3 font-medium whitespace-nowrap">Programa</th>
                    <th className="pb-2 pr-3 font-medium whitespace-nowrap">Período</th>
                    <th className="pb-2 pr-3 font-medium whitespace-nowrap">Status</th>
                    <th className="pb-2 pr-3 font-medium whitespace-nowrap">Motivo do término</th>
                  </tr>
                </thead>
                <tbody>
                  {pastAffiliations.map((row) => (
                    <tr key={row.id} className="border-b border-admin-100">
                      <td className="py-2.5 pr-3 text-admin-900">{row.program_name}</td>
                      <td className="py-2.5 pr-3 text-admin-700">
                        {formatDate(row.starts_at)} → {formatDate(row.ends_at)}
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className={statusChip(row.status)}>{STATUS_LABEL[row.status] || row.status}</span>
                      </td>
                      <td className="py-2.5 pr-3 text-admin-700">
                        {row.revoked_reason || (row.status === 'expired' ? 'Atingiu a data de término' : '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {modal && (
        <ProgramModal
          mode={modal}
          affiliation={modal === 'edit' ? active : null}
          programs={programs}
          durationChoices={data?.duration_choices}
          onSubmit={modal === 'edit' ? handleUpdate : handleCreate}
          onClose={() => setModal(null)}
        />
      )}

      {confirmRevoke && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-75"
              onClick={() => setConfirmRevoke(false)}
            />
            <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
              <h3 className="mb-2 text-lg font-semibold text-admin-900">
                Desativar este vínculo?
              </h3>
              <p className="mb-4 text-sm text-admin-600">
                O estúdio continua ativo — isso apenas encerra a cobertura da parceria. A partir de agora,
                o acesso volta a depender do pagamento, então o próximo pagamento recusado ou cancelado
                o suspenderá.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmRevoke(false)}
                  className="btn btn-secondary"
                  disabled={revoking}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRevoke}
                  className="btn btn-primary bg-red-600 hover:bg-red-700"
                  disabled={revoking}
                >
                  {revoking ? 'Desativando…' : 'Desativar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TenantProgramPanel
